import React, { useState, useRef, useMemo } from 'react';
import {
  format,
  parseISO,
  isBefore,
  startOfDay,
  getDay,
  addMonths,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameDay,
  isSameMonth,
} from 'date-fns';
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  AlertTriangle,
  Clock,
  Info,
  Lock,
} from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { EffortDot } from '../orka/date/EffortDot';
import { DayStatsTooltip } from '../orka/date/DayStatsTooltip';
import { useDateStats } from '../../hooks/useDateStats';
import { getEffortBucket } from '../../design-system/effortScale';

/**
 * DatePickerWithStats — V4 DataAwareDatePicker (Orka V4 spec).
 *
 * Canonical V4 rules applied here:
 *   1. The stats query spans the full 7 × N visible grid, not the month.
 *   2. Every visible cell (including adjacent-month cells) renders the
 *      same dot + tooltip treatment. No outside-month muting.
 *   3. Loading / stale / error / locked modes share one cell pipeline.
 *   4. Mobile surfaces pin the tooltip inline instead of floating.
 *   5. Optional decision bar when the selected day is already overloaded.
 *
 * Props:
 *   value          — ISO yyyy-MM-dd string (or undefined)
 *   onChange       — (iso|null) => void
 *   scope          — { actingAs, orgId } for multi-user/org cache
 *   triggerClassName
 *   triggerSize    — 'sm' | 'md'   (default 'md')
 *   triggerVariant — 'outline' | 'ghost' (default 'outline')
 *   locked         — boolean — disables selection, shows Locked banner
 *   pinnedTooltip  — force inline tooltip (used by mobile surfaces)
 *   showDecisionBar — when true, renders the overload warning for selected day
 *   ariaLabelTemplate — (date, stats) => string
 *   'data-testid'  — trigger test id (defaults to 'date-picker-stats-trigger')
 */
const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** Build the 7 × N visible grid anchored on Monday. */
function buildVisibleGrid(month) {
  const first = startOfMonth(month);
  const last = endOfMonth(month);
  const start = startOfWeek(first, { weekStartsOn: 1 });
  const end = endOfWeek(last, { weekStartsOn: 1 });
  const cells = [];
  let d = start;
  while (d <= end) {
    cells.push({ date: new Date(d), key: format(d, 'yyyy-MM-dd') });
    d = addDays(d, 1);
  }
  return cells;
}

const DatePickerWithStats = ({
  value,
  onChange,
  scope,
  triggerClassName,
  triggerSize = 'md',
  triggerVariant = 'outline',
  locked = false,
  pinnedTooltip = false,
  showDecisionBar = true,
  ariaLabelTemplate,
  'data-testid': testId = 'date-picker-stats-trigger',
}) => {
  const [open, setOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(value ? parseISO(value) : new Date());
  const [hoveredDate, setHoveredDate] = useState(null);
  const [tooltipRect, setTooltipRect] = useState(null);
  const containerRef = useRef(null);

  const { stats, state, range } = useDateStats(currentMonth, scope);

  const selectedDate = value ? parseISO(value) : undefined;
  const cells = useMemo(() => buildVisibleGrid(currentMonth), [currentMonth]);
  const hoveredStats = hoveredDate ? stats[hoveredDate] : null;
  const hoveredLoading = state === 'loading' && !hoveredStats;

  const selectedStats = value ? stats[value] : null;
  const selectedBucket = selectedStats ? getEffortBucket(selectedStats.total_effort) : null;
  const isOverloaded =
    selectedBucket && (selectedBucket.key === 'doom' || selectedBucket.key === 'overloaded');

  const handleSelect = (date) => {
    if (locked) return;
    onChange(format(date, 'yyyy-MM-dd'));
    setOpen(false);
  };

  const triggerSizeClass = triggerSize === 'sm' ? 'h-8 text-xs' : 'h-10 text-sm';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={triggerVariant}
          className={cn(
            'justify-start text-left font-mono orka-border-strong',
            triggerSizeClass,
            !value && 'orka-text-tertiary',
            locked && 'orka-bg-intent-soft orka-border-strong',
            triggerClassName,
          )}
          data-testid={testId}
        >
          {locked ? (
            <Lock className="mr-2 h-4 w-4" />
          ) : (
            <CalendarIcon className="mr-2 h-4 w-4" />
          )}
          {locked ? 'Date picker locked' : value || 'Pick a date'}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 z-[9999] orka-surface-raised orka-border-strong"
        align="start"
        sideOffset={4}
      >
        <div ref={containerRef} className="relative p-3 w-[360px]">
          {/* Header: month title + nav */}
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="font-semibold text-sm orka-text-primary">
                {format(currentMonth, 'LLLL yyyy')}
              </div>
              <div className="text-[10px] uppercase tracking-wider orka-text-tertiary mt-0.5 font-mono">
                {range.start_date} → {range.end_date}
              </div>
            </div>
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => setCurrentMonth((m) => addMonths(m, -1))}
                className="h-7 w-7 rounded-md flex items-center justify-center orka-border-default border hover:orka-surface-subtle"
                aria-label="Previous month"
                data-testid="date-picker-prev-month"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={() => setCurrentMonth((m) => addMonths(m, 1))}
                className="h-7 w-7 rounded-md flex items-center justify-center orka-border-default border hover:orka-surface-subtle"
                aria-label="Next month"
                data-testid="date-picker-next-month"
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          {/* Status banners */}
          {locked && (
            <div
              className="mb-2 flex items-center gap-2 px-2 py-1 rounded-md text-[11px] font-semibold orka-bg-intent-soft border orka-border-strong"
              data-testid="date-picker-locked-banner"
            >
              <Lock className="h-3 w-3" /> Tasks module locked
            </div>
          )}
          {state === 'error' && (
            <div
              className="mb-2 flex items-center gap-2 px-2 py-1 rounded-md text-[11px] font-semibold orka-bg-danger-soft border orka-border-strong"
              data-testid="date-picker-error-banner"
            >
              <AlertTriangle className="h-3 w-3" /> Couldn&rsquo;t load stats — retry
            </div>
          )}
          {state === 'stale' && (
            <div
              className="mb-2 flex items-center gap-2 px-2 py-1 rounded-md text-[11px] font-semibold orka-bg-warning-soft border orka-border-strong"
              data-testid="date-picker-stale-banner"
            >
              <Clock className="h-3 w-3" /> Refreshing visible grid stats…
            </div>
          )}

          {/* Weekday header */}
          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w) => (
              <div
                key={w}
                className="h-5 flex items-center justify-center text-[9px] font-extrabold uppercase tracking-wider orka-text-tertiary"
              >
                {w}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1" data-testid="date-picker-grid">
            {cells.map((cell) => {
              const dayStats = stats[cell.key];
              const bucket = dayStats ? getEffortBucket(dayStats.total_effort) : null;
              const weekend = getDay(cell.date) === 0 || getDay(cell.date) === 6;
              const isToday = isSameDay(cell.date, new Date());
              const isSelected = selectedDate && isSameDay(cell.date, selectedDate);
              const isPast = isBefore(cell.date, startOfDay(new Date()));
              const inCurrentMonth = isSameMonth(cell.date, currentMonth);
              const overloaded = bucket?.key === 'doom' || bucket?.key === 'overloaded';
              const missed =
                isPast &&
                dayStats &&
                dayStats.task_count > 0 &&
                (dayStats.incomplete_count > 0 ||
                  dayStats.task_count -
                    (dayStats.done_count || 0) -
                    (dayStats.meeting_count || 0) >
                    0);
              const dotState =
                state === 'error'
                  ? 'error'
                  : state === 'loading' && !dayStats
                    ? 'loading'
                    : !dayStats || dayStats.total_effort === 0
                      ? 'empty'
                      : 'loaded';

              const aria = ariaLabelTemplate
                ? ariaLabelTemplate(cell.date, dayStats)
                : `${cell.key} — ${
                    state === 'loading' && !dayStats
                      ? 'loading day stats'
                      : `${bucket?.meaning || 'nothing planned'}, ${dayStats?.task_count || 0} tasks, ${dayStats?.total_effort || 0} effort points`
                  }`;

              const handleEnter = (e) => {
                setHoveredDate(cell.key);
                if (!pinnedTooltip) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const containerRect = containerRef.current?.getBoundingClientRect();
                  if (containerRect) {
                    setTooltipRect({
                      top: rect.top - containerRect.top,
                      left: rect.left - containerRect.left + rect.width / 2,
                    });
                  }
                }
              };
              const handleLeave = () => {
                if (!pinnedTooltip) {
                  setHoveredDate(null);
                  setTooltipRect(null);
                }
              };

              return (
                <button
                  type="button"
                  key={cell.key}
                  disabled={locked}
                  onClick={() => handleSelect(cell.date)}
                  onMouseEnter={handleEnter}
                  onMouseLeave={handleLeave}
                  onFocus={handleEnter}
                  aria-label={aria}
                  data-testid={`date-picker-day-${cell.key}`}
                  data-in-month={inCurrentMonth ? 'true' : 'false'}
                  className={cn(
                    'relative h-10 rounded-md flex flex-col items-center justify-center',
                    'border border-transparent orka-transition-colors',
                    'hover:-translate-y-px hover:orka-border-strong',
                    weekend && !isSelected && 'orka-bg-info-soft opacity-50',
                    isToday && !isSelected && 'orka-bg-info-soft border orka-border-strong font-bold',
                    isSelected && 'orka-bg-intent orka-text-inverse font-bold',
                    overloaded && !isSelected && 'orka-border-strong animate-pulse',
                    state === 'stale' && 'opacity-60',
                    locked && 'opacity-50 cursor-not-allowed',
                  )}
                  style={
                    missed
                      ? {
                          backgroundImage:
                            'repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(156,163,175,0.25) 2px, rgba(156,163,175,0.25) 4px)',
                        }
                      : undefined
                  }
                >
                  <span className="text-xs font-semibold leading-none">
                    {format(cell.date, 'd')}
                  </span>
                  <span className="mt-1">
                    <EffortDot stats={dayStats} state={dotState} size="xs" />
                  </span>
                </button>
              );
            })}
          </div>

          {/* Floating tooltip (desktop) */}
          {hoveredDate && tooltipRect && !pinnedTooltip && (hoveredStats || hoveredLoading) && (
            <div
              className="absolute z-[100] pointer-events-none"
              style={{
                top: tooltipRect.top - 4,
                left: tooltipRect.left,
                transform: 'translate(-50%, -100%)',
              }}
            >
              <DayStatsTooltip
                date={hoveredDate}
                stats={hoveredStats}
                loading={hoveredLoading}
                variant="floating"
              />
            </div>
          )}

          {/* Pinned tooltip (mobile / touch) */}
          {pinnedTooltip && hoveredDate && (hoveredStats || hoveredLoading) && (
            <div className="mt-3" data-testid="date-picker-tooltip-pinned">
              <DayStatsTooltip
                date={hoveredDate}
                stats={hoveredStats}
                loading={hoveredLoading}
                variant="inline"
              />
            </div>
          )}

          {/* Optional overload decision bar */}
          {showDecisionBar && value && isOverloaded && (
            <div
              className="mt-3 rounded-lg border p-3 flex items-start gap-3 orka-bg-warning-soft orka-border-strong"
              data-testid="date-picker-decision-bar"
            >
              <AlertTriangle className="h-4 w-4 orka-text-warning mt-0.5" />
              <div className="flex-1 text-[11px] leading-snug">
                <div className="font-semibold orka-text-warning">
                  This day is already at {selectedStats?.total_effort || 0} effort points.
                </div>
                <div className="orka-text-secondary mt-0.5">
                  {selectedBucket.meaning === 'strongly discourage'
                    ? 'Strongly discourage adding more unless this is genuinely urgent.'
                    : 'This day looks heavy. Consider another day if possible.'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => onChange?.(null)}
                className="text-[11px] font-semibold px-2 py-1 rounded-md orka-border-default border hover:orka-surface-subtle"
                data-testid="date-picker-decision-choose-other"
              >
                Choose another day
              </button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerWithStats;
